import { ReactNode, useState } from "react";
import cx from "classnames";

import { IconComponent } from "types/Icon";
import Box from "ds/components/Box";
import { ShiftSelectionAction } from "components/BulkActions/useShiftSelectionManager";

import { TableSelectionStore } from "../../store/useTableSelectionStore/types";
import styles from "./styles.module.css";
import TableCell, { TableCellProps } from "../Cell";
import TableCellContentActions from "./Actions";
import TableCellContentSelection from "./Selection";

type TableCellActionBase = {
  icon: IconComponent;
  title: string;
  onPress?: () => void;
};

type TableCellActionButton = TableCellActionBase & {
  onPress: () => void;
  href?: never;
};

type TableCellActionLink = TableCellActionBase & {
  href: string;
  onPress?: () => void;
};

export type TableCellAction = TableCellActionButton | TableCellActionLink;

export type TableCellContentProps = {
  id: string;
  children?: ReactNode;
  actions?: TableCellAction[];
  className?: string;
  selectionStore?: TableSelectionStore;
  selectionId?: string;
  hasMoreSelectionOptions?: boolean;
  shiftSelectionAction?: ShiftSelectionAction;
} & Pick<TableCellProps, "role" | "stickyRight" | "stickyLeft">;

const TableCellContent = ({
  id,
  children,
  actions,
  role,
  stickyRight,
  stickyLeft,
  className,
  selectionStore,
  selectionId,
  hasMoreSelectionOptions,
  shiftSelectionAction,
}: TableCellContentProps) => {
  const [isActive, setIsActive] = useState(false);

  const hasActions = actions && actions.length > 0;
  const withSelection = selectionStore && selectionId;

  return (
    <TableCell
      id={id}
      className={cx(
        styles.cellContent,
        { [styles.interactive]: hasActions },
        { [styles.active]: isActive },
        className
      )}
      role={role}
      stickyRight={stickyRight}
      stickyLeft={stickyLeft}
      padding="x-large"
      align="center"
    >
      <Box direction="row" align="center" gap="xx-large" fullWidth>
        {withSelection && (
          <TableCellContentSelection
            selectionStore={selectionStore}
            selectionId={selectionId}
            hasMoreSelectionOptions={hasMoreSelectionOptions}
            shiftSelectionAction={shiftSelectionAction}
          />
        )}
        <Box direction="row" align="center" justify="between" gap="medium" fullWidth>
          <Box fullWidth>{children}</Box>
          {hasActions && <TableCellContentActions actions={actions} onOpenChange={setIsActive} />}
        </Box>
      </Box>
    </TableCell>
  );
};

TableCellContent.displayName = "DS.Table.CellContent";

export default TableCellContent;
